import { buysSnack } from '../domain/happiness.js';
import { inquiryPoint, boardingWorkPoint } from '../domain/queues.js';
import { reserveGate } from "../services/operations.js";
import { tier } from "../domain/airports.js";
import { System, SystemImpl } from "minecs";
import { shiftsActive, tierSpeed, RETAIL_MODULES } from "../domain/act2.js";
import { deskStaffFactor, travellerRoute } from "../services/act2.js";
import { crisisSpeed } from "../services/events.js";
import { archetype, bagSerial, BUSINESS_FARE, FAMILY_BAGS } from "../domain/passengers.js";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportStation, AirportPassenger, AirportStaff, AirportAgent, AirportBag, AirportTrash } from "../components/index.js";
import { CONFIG, onPad, stationPad, RACK, RACK_PICKUP, nextStage, PASSPORT, internationalTraveller } from "../domain/config.js";
import { payout, deposit, passportFee } from "../domain/economy.js";
import { state, career, ids, stations, distance, make, resetPatience } from "../services/world.js";
import { worksDesk } from '../services/crew.js';
import { RACK_LIMIT, NEXT_LINE_ROOM } from '../services/holds.js';
@System(AirportStation)
export class AirportService extends SystemImpl<GameModel> {
    static depth = 4400;
    run = (g: GameModel, id: number) => {
        const e = state(g), s = g.getTypedUnsafe(AirportStation, id);
        if (!e.loaded || !s.unlocked)
            return;
        const pads = s.index === 6 ? Array.from({ length: s.lanes }, (_, gate) => boardingWorkPoint(gate, tier(e).code)) : [stationPad(s.index, 'work')];
        const atWork = (id: number) => pads.some(work => onPad('work', g.getTypedUnsafe(Transform, id), work));
        const workers = ids(g, AirportAgent).filter(a => atWork(a));
        const deskStaff = ids(g, AirportStaff).filter(a => { const f = g.getTypedUnsafe(AirportStaff, a); return worksDesk(f) && f.stage === s.index && atWork(a); });
        const clerk = deskStaff.length;
        // Staff at this desk: skill pips (MGT-003), a Quick Hands trait (MGT-001) and a captain's rally nearby (MGT-005).
        const quick = deskStaff.length ? deskStaffFactor(g, deskStaff, s.index) : 1;
        s.activeWorkers = workers.length + clerk;
        if (!s.activeWorkers)
            return;
        for (const a of workers)
            g.getTypedUnsafe(AirportAgent, a).worked++;
        if (workers.length) {
            e.workFrames++;
            e.handsOffFlights = 0;
        }
        if (s.index === 1 && ids(g, AirportBag).length >= RACK_LIMIT)
            return;
        const ss = stations(g);
        // International travellers route through Passport Control (MGT-009); queue room is checked per destination.
        const nextCounts = new Map<number, number>();
        const countAt = (index: number) => nextCounts.get(index) ?? (nextCounts.set(index, ss[index]?.unlocked ? ids(g, AirportPassenger).filter(a => { const p = g.getTypedUnsafe(AirportPassenger, a); return p.stage === index && p.state !== 3; }).length : 0), nextCounts.get(index)!);
        const nearbyLitter = ids(g, AirportTrash).filter(t => g.getTypedUnsafe(AirportTrash, t).carrier < 0 && distance(g.getTypedUnsafe(Transform, t), CONFIG.stages[s.index]) < 240).length;
        const cleanliness = Math.max(0.75, 1 - nearbyLitter * 0.025);
        // ECON-005: staff unpaid for two shifts work 20% slower; a captain at the desk keeps full speed.
        const unpaid = e.unpaidShifts >= 2 && workers.length === 0 ? 0.8 : 1;
        const speed = (1 + Math.min(2, s.activeWorkers - 1) * 0.55) * (e.expressUntil > g.frame ? 2 : 1) * cleanliness * unpaid * quick * (shiftsActive(e) ? crisisSpeed(e, s.index) * tierSpeed(e[`tier${s.index}` as 'tier0']) : 1);
        for (const pid of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, pid);
            if (p.stage !== s.index || p.state !== 2)
                continue;
            let passengerSpeed = speed;
            if (s.index === 6) {
                const work = boardingWorkPoint(p.gate, tier(e).code);
                const gateCaptains = workers.filter(a => onPad('work', g.getTypedUnsafe(Transform, a), work));
                const gateStaff = deskStaff.filter(a => onPad('work', g.getTypedUnsafe(Transform, a), work));
                if (!gateCaptains.length && !gateStaff.length || ids(g, AirportPassenger).some(a => { const q = g.getTypedUnsafe(AirportPassenger, a); return q.state === 7 && q.gate === p.gate && distance(g.getTypedUnsafe(Transform, a), inquiryPoint(p.gate, tier(e).code)) < 8; })) continue;
                passengerSpeed = (1 + Math.min(2, gateCaptains.length + gateStaff.length - 1) * 0.55) * (e.expressUntil > g.frame ? 2 : 1) * cleanliness * (gateCaptains.length ? 1 : unpaid) * (gateStaff.length ? deskStaffFactor(g, gateStaff, 6) : 1);
            }
            p.remaining = Math.max(0, p.remaining - passengerSpeed);
            if (p.remaining > 0)
                continue;
            if (s.index === 1 && ids(g, AirportBag).length >= RACK_LIMIT)
                continue;
            const nextIndex = nextStage(s.index, ss, travellerRoute(g, p.serial, p.vip)), next = ss[nextIndex], kind = archetype(p.serial, p.vip, shiftsActive(e));
            if (next?.unlocked && countAt(nextIndex) >= NEXT_LINE_ROOM)
                continue; // Hold served passengers until downstream queue has space.
            if (s.index === 6) { const gate = reserveGate(g, p.gate); if (gate < 0) continue; p.gate = gate; }
            // Passport Control charges international travellers a fee; it is never a free income desk (MGT-009).
            const amount = (s.index === PASSPORT ? (internationalTraveller(p.serial, ss) ? passportFee(e) : 0) : payout(s, e, g.frame, career(g).prestige) * (s.index === 4 && shiftsActive(e) ? RETAIL_MODULES[e.retailModule].spend : 1)) * (p.vip ? 2 : 1) * (s.index === 0 && kind === 'business' ? BUSINESS_FARE : 1);
            const posted = deposit(e, s, amount);
            if (shiftsActive(e)) { e.shiftEarned += posted; if (s.index === 4 || s.index === 5) e.shiftRetail += posted; if (s.index === 6) e.shiftServed++; }
            s.served++;
            e.served++;
            if (s.index === 0 && workers.length) e.manualCheckIns++;
            if (s.index === 1) {
                // Families check two bags (MGT-012).
                for (let bag = 0; bag < (kind === 'family' ? FAMILY_BAGS : 1); bag++) {
                    const n = ids(g, AirportBag).filter(b => g.getTypedUnsafe(AirportBag, b).carrier < 0).length;
                    make(g, 'Bag', { AirportBag: { serial: bagSerial(p.serial, bag) }, Transform: { x: RACK_PICKUP.x - 18 + (n % 3) * 18, y: RACK_PICKUP.y + Math.floor(n / 3) * 12 } });
                }
            }

            const snack = s.index === 0 && buysSnack(p.serial, p.happiness);
            p.happiness = Math.min(100, p.happiness + 5);
            p.slot = -1;
            p.remaining = 0;
            if (next?.unlocked) {
                p.stage = nextIndex;
                p.state = s.index === 2 ? 5 : nextIndex === 6 ? 6 : 0;
                resetPatience(p);
                if (p.state === 5) p.slot = 0;
                nextCounts.set(nextIndex, countAt(nextIndex) + 1);
            }
            else {
                p.state = 3; p.walkStep = 0; p.seat = -1; p.seated = false;
            }
            // One in six buys a snack at the machine, then carries it and drops the wrapper
            // on the floor when they join the Security line (handled in the routing pass).
            if (snack) {
                const machines = tier(state(g)).vending;
                p.vending = machines[g.rand.int(0, machines.length - 1)];
                p.shopFrames = 90;
                p.state = 4;
            }
        }
    };
}
